export interface SearchGoal {
  id: string;
  parentId: string | null;
  title: string;
  description?: string | null;
  dod?: string | null;
  status?: string | null;
  deleted?: boolean;
}

export interface SearchTask {
  id: string;
  title: string;
  description?: string | null;
  completed?: boolean;
  deleted?: boolean;
  goalId?: string | null;
  projectId?: string | null;
}

export type SearchResultKind = "goal" | "task";

export interface SearchResult {
  kind: SearchResultKind;
  id: string;
  title: string;
  subtitle: string;
  score: number;
  goalId?: string | null;
  projectId?: string | null;
  completed?: boolean;
}

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function scoreText(query: string, primary: string, secondary: string[]): number | null {
  const p = normalize(primary);
  if (p.startsWith(query)) return 0;
  if (p.includes(query)) return 1;
  return secondary.some((s) => normalize(s).includes(query)) ? 2 : null;
}

function goalPath(byId: Map<string, SearchGoal>, id: string): string {
  const path: string[] = [];
  let cur = byId.get(id) ?? null;
  let hops = 0;
  while (cur && hops < 100) {
    path.unshift(cur.title);
    cur = cur.parentId ? byId.get(cur.parentId) ?? null : null;
    hops += 1;
  }
  return path.join(" / ");
}

export function searchFlow(queryRaw: string, goals: SearchGoal[], tasks: SearchTask[], limit = 24): SearchResult[] {
  const query = normalize(queryRaw);
  if (!query) return [];
  const goalsById = new Map(goals.map((g) => [g.id, g]));

  const goalResults: SearchResult[] = goals
    .filter((g) => !g.deleted)
    .map((g): SearchResult | null => {
      const score = scoreText(query, g.title, [g.description ?? "", g.dod ?? ""]);
      if (score == null) return null;
      return {
        kind: "goal" as const,
        id: g.id,
        title: g.title,
        subtitle: g.status === "dormant" ? `${goalPath(goalsById, g.id)} / 休眠` : goalPath(goalsById, g.id),
        score,
      };
    })
    .filter((r): r is SearchResult => r !== null);

  const taskResults: SearchResult[] = tasks
    .filter((t) => !t.deleted)
    .map((t): SearchResult | null => {
      const score = scoreText(query, t.title, [t.description ?? ""]);
      if (score == null) return null;
      return {
        kind: "task" as const,
        id: t.id,
        title: t.title,
        subtitle: t.completed ? "完了済みタスク" : "タスク",
        score,
        goalId: t.goalId ?? null,
        projectId: t.projectId ?? null,
        completed: Boolean(t.completed),
      };
    })
    .filter((r): r is SearchResult => r !== null);

  return [...goalResults, ...taskResults]
    .sort((a, b) => a.score - b.score || (a.kind === b.kind ? 0 : a.kind === "goal" ? -1 : 1) || a.title.localeCompare(b.title))
    .slice(0, limit);
}
